import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ChatConversation } from "@/components/ui/chat-bubble";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ConversationMessage, Lead, MediaState, UIState, Violation } from "@/types/video-call";
import {
  ChevronDown,
  ChevronUp,
  Info,
  MessageCircle,
  Mic,
  MicOff,
  Monitor,
  PhoneOff,
  ScreenShare,
  ScreenShareOff,
  Video,
  VideoOff,
  Volume2,
  VolumeX,
  WifiOff,
} from "lucide-react";
import audioLines from "../../assets/audio-lines.svg";
import audioSpeaking from "../../assets/audio-lines-speaking.svg";
import React from "react";
import Lottie from "lottie-react";
import hirevoxLogoAnimation from "../../assets/hirevox-logo-animation.json";
import { TabChangePenaltyOverlay } from "./overlays/TabChangePenaltyOverlay";
import { ScreenShareEndedWarning } from "./overlays/ScreenShareEndedWarning";
import { WindowBlurPenaltyOverlay } from "./overlays/WindowBlurPenaltyOverlay";
import { CameraDisconnectedWarning } from "./overlays/CameraDisconnectedWarning";
import { NetworkDisconnectedWarning } from "./overlays/NetworkDisconnectedWarning";
import { ConnectionQualityIndicator } from "./overlays/ConnectionQualityIndicator";
import { PoorConnectionWarning } from "./overlays/PoorConnectionWarning";
import { ExternalMonitorWarning } from "./overlays/ExternalMonitorWarning";
import { AgentConnectionOverlay } from "./overlays/AgentConnectionOverlay";
import { ViolationsPanel } from "./ViolationsPanel";
import { filterTrackingPhrases } from "@/utils/transcriptFiltering";
import * as Sentry from "@sentry/react";

interface VideoCallInterfaceProps {
  lead: Lead;
  connected: boolean;
  connecting: boolean;
  callActive: boolean;
  remoteParticipants: any[];
  mediaState: MediaState;
  currentSubtitle: string;
  showSubtitles: boolean;
  isAgentSpeaking: boolean;
  conversation: ConversationMessage[];
  showChatWindow: boolean;
  callDuration: string;
  networkStatus?: 'connected' | 'disconnected';
  connectionQuality?: 'excellent' | 'good' | 'poor' | 'unknown';
  localVideoRef: React.RefObject<HTMLVideoElement>;
  remoteVideoRef: React.RefObject<HTMLVideoElement>;
  remoteAudioRef: React.RefObject<HTMLAudioElement>;
  screenShareRef: React.RefObject<HTMLVideoElement>;
  secondaryCameraRef?: React.RefObject<HTMLVideoElement>;
  siriWaveRef: React.RefObject<HTMLDivElement>;
  onToggleMicrophone: () => void;
  onToggleVideo: () => void;
  onToggleSpeaker: () => void;
  onToggleSubtitles: () => void;
  onToggleChatWindow: () => void;
  onStartScreenShare: () => void;
  onStopScreenShare: () => void;
  onEndCall: (reason?: string) => void;
  screenSharingEnabled: boolean;
  showTabChangePenalty?: boolean;
  onTabChangePenaltyComplete?: () => void;
  onDismissScreenShareWarning?: () => void;
  showWindowBlurPenalty?: boolean;
  onWindowBlurPenaltyComplete?: () => void;
  onDismissCameraWarning?: () => void;
  onReconnectCamera?: () => void;
  violations?: Violation[];
  showViolationsPanel?: boolean;
  isInterviewSession?: boolean;
  agentConnected?: boolean;
  agentConnecting?: boolean;
  agentDisconnected?: boolean;
  agentDisconnectReason?: string;
  isCallEnding?: boolean;
  showExternalMonitorWarning?: boolean;
  externalMonitorScreenCount?: number;
  externalMonitorPreviousScreenCount?: number;
  onDismissExternalMonitorWarning?: () => void;
  callId?: string;
  organizationId?: string;
}

type LayoutMode = "side-by-side" | "picture-in-picture" | "focus-screen";

export const VideoCallInterface: React.FC<VideoCallInterfaceProps> = ({
  lead,
  connected,
  connecting,
  callActive,
  remoteParticipants,
  mediaState,
  currentSubtitle,
  showSubtitles,
  isAgentSpeaking,
  conversation,
  showChatWindow,
  callDuration,
  networkStatus = 'connected',
  connectionQuality = 'unknown',
  localVideoRef,
  remoteVideoRef,
  remoteAudioRef,
  screenShareRef,
  secondaryCameraRef,
  siriWaveRef,
  onToggleMicrophone,
  onToggleVideo,
  onToggleSpeaker,
  onToggleSubtitles,
  onToggleChatWindow,
  onStartScreenShare,
  onStopScreenShare,
  onEndCall,
  screenSharingEnabled,
  showTabChangePenalty = false,
  onTabChangePenaltyComplete,
  onDismissScreenShareWarning,
  showWindowBlurPenalty = false,
  onWindowBlurPenaltyComplete,
  onDismissCameraWarning,
  onReconnectCamera,
  violations = [],
  isInterviewSession = false,
  agentConnected = false,
  agentConnecting = false,
  agentDisconnected = false,
  agentDisconnectReason,
  isCallEnding = false,
  showViolationsPanel = true,
  showExternalMonitorWarning = false,
  externalMonitorScreenCount = 0,
  externalMonitorPreviousScreenCount = 0,
  onDismissExternalMonitorWarning,
  callId,
  organizationId,
}) => {
  const [layoutMode, setLayoutMode] =
    React.useState<LayoutMode>("side-by-side");
  const [feedsSwapped, setFeedsSwapped] = React.useState(false);
  const [showEndCallDialog, setShowEndCallDialog] = React.useState(false);
  const [hasLocalStream, setHasLocalStream] = React.useState(false);
  const [isUserSpeaking, setIsUserSpeaking] = React.useState(false);
  const [isScreenShareExpanded, setIsScreenShareExpanded] = React.useState(false);
  const internalSecondaryCameraRef = React.useRef<HTMLVideoElement>(null);
  const effectiveSecondaryCameraRef = secondaryCameraRef || internalSecondaryCameraRef;
  const videoEnabledTimeRef = React.useRef<number>(0);
  const agentSpeechScrollRef = React.useRef<HTMLDivElement>(null);
  const shouldShowAgentConnecting =
    !isCallEnding &&
    (connecting ||
      (callActive && (!connected && !agentDisconnected)) ||
      (callActive && connected && !isAgentSpeaking && conversation.length === 0));

  
  // Helper function to safely clean up video element streams
  const cleanupVideoElement = React.useCallback((videoElement: HTMLVideoElement | null) => {
    if (videoElement && videoElement.srcObject) {
      const stream = videoElement.srcObject as MediaStream;
      stream.getTracks().forEach(track => {
        track.stop();
      });
      videoElement.srcObject = null;
    }
  }, []);

  // Copy camera stream to secondary ref when screen sharing is active
  React.useEffect(() => {
    if (mediaState.screenSharingActive && remoteVideoRef.current && effectiveSecondaryCameraRef.current) {
      const stream = remoteVideoRef.current.srcObject;
      if (stream && effectiveSecondaryCameraRef.current.srcObject !== stream) {
        // Clean up existing stream before assigning new one
        cleanupVideoElement(effectiveSecondaryCameraRef.current);
        effectiveSecondaryCameraRef.current.srcObject = stream;
      }
    } else if (!mediaState.screenSharingActive && effectiveSecondaryCameraRef.current) {
      // Clean up secondary camera when screen sharing stops
      cleanupVideoElement(effectiveSecondaryCameraRef.current);
    }
  }, [mediaState.screenSharingActive, remoteVideoRef, effectiveSecondaryCameraRef, cleanupVideoElement]);
  
  // Track when video becomes enabled for timing fallbacks
  React.useEffect(() => {
    if (mediaState.isVideoEnabled && !videoEnabledTimeRef.current) {
      videoEnabledTimeRef.current = Date.now();
    } else if (!mediaState.isVideoEnabled) {
      videoEnabledTimeRef.current = 0; // Reset when video is disabled
    }
  }, [mediaState.isVideoEnabled]);
  
  // Monitor when video element actually has a stream (LiveKit compatible)
  React.useEffect(() => {
    const checkVideoStream = () => {
      if (localVideoRef.current && mediaState.isVideoEnabled) {
        const stream = localVideoRef.current.srcObject as MediaStream;
        let isValidStream = false;
        
        // Primary check: Do we have a MediaStream with active video tracks?
        if (stream && stream.active) {
          const videoTracks = stream.getVideoTracks();
          if (videoTracks.length > 0) {
            const track = videoTracks[0];
            // Check if track is enabled and live
            isValidStream = track.enabled && track.readyState === 'live';
          }
        }
        
        // Fallback check: LiveKit might manage video differently
        // If we don't have srcObject but video element has dimensions and is playing
        if (!isValidStream && !stream) {
          const hasVideoData = localVideoRef.current.videoWidth > 0 && localVideoRef.current.videoHeight > 0;
          const isPlaying = !localVideoRef.current.paused && !localVideoRef.current.ended;
          isValidStream = hasVideoData && isPlaying;
        }
        
        // Final fallback: Check if enough time has passed since video was enabled
        if (!isValidStream && !stream && mediaState.isVideoEnabled && videoEnabledTimeRef.current) {
          const timeSinceVideoEnabled = Date.now() - videoEnabledTimeRef.current;
          if (timeSinceVideoEnabled > 2000) { // After 2 seconds, be more permissive
            isValidStream = true;
          }
        }
        
        // Track camera state changes
        if (isValidStream !== hasLocalStream) {
          // Camera state changed
        }
        
        setHasLocalStream(isValidStream);
      } else {
        setHasLocalStream(false);
      }
    };

    // Check immediately
    checkVideoStream();

    // Set up an interval to check periodically - reduced frequency for stability
    const interval = setInterval(checkVideoStream, 1000);

    // Also listen for video element events
    const videoElement = localVideoRef.current;
    const handleLoadedData = () => checkVideoStream();
    const handlePlay = () => checkVideoStream();
    const handleLoadedMetadata = () => checkVideoStream();

    if (videoElement) {
      videoElement.addEventListener('loadeddata', handleLoadedData);
      videoElement.addEventListener('play', handlePlay);
      videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
    }

    return () => {
      clearInterval(interval);
      if (videoElement) {
        videoElement.removeEventListener('loadeddata', handleLoadedData);
        videoElement.removeEventListener('play', handlePlay);
        videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      }
    };
  }, [mediaState.isVideoEnabled, hasLocalStream]);


  // Fallback camera detection - trust mediaState after timeout
  React.useEffect(() => {
    if (mediaState.isVideoEnabled && !hasLocalStream) {
      // Give LiveKit up to 3 seconds to initialize the camera, then trust the mediaState
      const fallbackTimer = setTimeout(() => {
        if (mediaState.isVideoEnabled && !hasLocalStream) {
          setHasLocalStream(true);
        }
      }, 3000);

      return () => clearTimeout(fallbackTimer);
    }
  }, [mediaState.isVideoEnabled, hasLocalStream]);
  
  // Cleanup video elements on unmount
  React.useEffect(() => {
    return () => {
      cleanupVideoElement(localVideoRef.current);
      cleanupVideoElement(remoteVideoRef.current);
      cleanupVideoElement(screenShareRef.current);
      cleanupVideoElement(effectiveSecondaryCameraRef.current);
    };
  }, [cleanupVideoElement, localVideoRef, remoteVideoRef, screenShareRef, effectiveSecondaryCameraRef]);

  // Calculate violation data - COMMENTED OUT: Not ending calls on 3 violations anymore
  // const maxViolations = 3;
  // const tabChangeCount = violations?.filter(v => v.type === "tab_change").length || 0;
  // const windowBlurCount = violations?.filter(v => v.type === "window_blur").length || 0;
  // const violationCount = tabChangeCount + windowBlurCount; // Combined total, not maximum


  // User speaking detection - for production, this should use actual audio level from useMediaControls
  React.useEffect(() => {
    if (mediaState.isMuted || !connected) {
      setIsUserSpeaking(false);
      return;
    }

    // TODO: Replace with actual audio level detection from useMediaControls onAudioLevelChange
    // For now, simulate based on conversation patterns - user speaks when not agent speaking
    let speakingTimer: NodeJS.Timeout;
    
    if (!isAgentSpeaking && connected && !mediaState.isMuted) {
      // Simulate natural speaking patterns
      const startSpeaking = () => {
        setIsUserSpeaking(true);
        // Speak for 2-5 seconds
        const speakDuration = 2000 + Math.random() * 3000;
        speakingTimer = setTimeout(() => {
          setIsUserSpeaking(false);
          // Pause for 3-8 seconds before potentially speaking again
          const pauseDuration = 3000 + Math.random() * 5000;
          speakingTimer = setTimeout(startSpeaking, pauseDuration);
        }, speakDuration);
      };

      // Start speaking after a random delay (1-6 seconds)
      const initialDelay = 1000 + Math.random() * 5000;
      speakingTimer = setTimeout(startSpeaking, initialDelay);
    }

    return () => {
      if (speakingTimer) {
        clearTimeout(speakingTimer);
      }
    };
  }, [mediaState.isMuted, connected, isAgentSpeaking]);

  // Control wave animation opacity based on agent speaking state
  React.useEffect(() => {
    if (siriWaveRef.current) {
      if (isAgentSpeaking && !agentDisconnected) {
        siriWaveRef.current.style.opacity = '0.3';
      } else {
        siriWaveRef.current.style.opacity = '0';
      }
    }
  }, [isAgentSpeaking, agentDisconnected]);

  // Get all agent messages for display - combine consecutive agent messages and filter tracking phrases
  const allAgentMessages = React.useMemo(() => {
    if (!conversation || conversation.length === 0) return [];
    
    // Get all assistant messages and group consecutive ones
    const assistantMessages: string[] = [];
    let currentCombinedMessage = '';
    
    for (let i = 0; i < conversation.length; i++) {
      const msg = conversation[i];
      
      if (msg.role === 'assistant') {
        // Filter tracking phrases from the message before combining
        const filteredMessage = filterTrackingPhrases(msg.message);
        
        // Only add non-empty filtered messages
        if (filteredMessage.trim()) {
          if (currentCombinedMessage) {
            currentCombinedMessage += ' ' + filteredMessage;
          } else {
            currentCombinedMessage = filteredMessage;
          }
        }
      } else {
        // Non-assistant message - save any pending combined message
        if (currentCombinedMessage) {
          assistantMessages.push(currentCombinedMessage);
          currentCombinedMessage = '';
        }
      }
    }
    
    // Don't forget the last combined message if it exists
    if (currentCombinedMessage) {
      assistantMessages.push(currentCombinedMessage);
    }
    
    return assistantMessages;
  }, [conversation]);

  // Auto-scroll agent speech to bottom when new messages arrive
  React.useEffect(() => {
    if (agentSpeechScrollRef.current && allAgentMessages.length > 0) {
      // Try multiple selectors for the scroll viewport
      const scrollElement = 
        agentSpeechScrollRef.current.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement ||
        agentSpeechScrollRef.current.querySelector('.scroll-area-viewport') as HTMLElement ||
        agentSpeechScrollRef.current.querySelector('[role="region"]') as HTMLElement ||
        agentSpeechScrollRef.current;
        
      if (scrollElement) {
        // Use requestAnimationFrame to ensure the DOM has updated
        requestAnimationFrame(() => {
          // Try multiple scroll methods to ensure it works
          scrollElement.scrollTop = scrollElement.scrollHeight;
          // Fallback: scroll using scrollTo if available
          if (scrollElement.scrollTo) {
            scrollElement.scrollTo({ top: scrollElement.scrollHeight, behavior: 'smooth' });
          }
        });
      }
    }
  }, [allAgentMessages]);

  return (
    <div className="h-screen bg-[#131314] text-white flex flex-col overflow-hidden">
      {/* Header */}
      <div className="w-full h-[72px] px-6 py-5 bg-[#131314] border-b border-white/5 flex justify-between items-center flex-shrink-0">
        <div className="flex justify-center items-center gap-3">
          <div className="text-white text-2xl font-medium leading-loose">
            {isInterviewSession ? 'AI Interview Session' : `Video Call with ${lead.first_name} ${lead.last_name}`}
          </div>
          <div className="px-3 py-0.5 bg-good-bg rounded-xl flex justify-center items-center animate-pulse">
            <div className="text-center text-good text-sm font-medium">
              {connected ? "Live" : connecting ? "Connecting..." : "Disconnected"}
            </div>
          </div>
          {networkStatus === 'disconnected' && (
            <Badge variant="destructive" className="bg-mid text-background animate-pulse flex items-center gap-1">
              <WifiOff className="h-3 w-3" />
              Network Lost
            </Badge>
          )}
          <ConnectionQualityIndicator quality={connectionQuality} />
        </div>
        <div className="flex justify-center items-center gap-3">
          <div className="w-5 h-5 bg-[#ec4040]/20 rounded-full flex justify-center items-center">
            <div className="w-2 h-2 bg-[#ec4040] rounded-full animate-pulse"></div>
          </div>
          <div className="text-white text-base font-medium leading-[21.12px]">
            {callDuration || "00:00"}
          </div>

        </div>
      </div>

      <div className="flex-1 p-6 min-h-0 flex flex-col items-center justify-center relative">
        <div className="w-full flex flex-col gap-4 min-h-0 h-full max-w-6xl">
          {/* Agent Speech Section - Above video containers */}
          <div className="flex flex-col gap-2 flex-shrink-0 min-h-32 max-h-48">
            <div className="text-white text-sm font-medium leading-[18.48px] text-center">
              Agent Speech:
            </div>
            <div className="w-full flex-1 bg-[#343537] rounded-2xl border border-border overflow-hidden">
              <ScrollArea ref={agentSpeechScrollRef} className="h-full w-full p-4">
                <div className="space-y-3">
                  {allAgentMessages.length > 0 ? (
                    allAgentMessages.map((message, index) => (
                      <div key={index} className="text-white text-sm leading-relaxed text-center">
                        <div className="bg-[#2a2b2d] rounded-lg p-3 border-l-2 border-[#d894fb] break-words whitespace-pre-wrap text-center">
                          {message}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-ink-faint text-sm text-center">
                      Waiting for agent to speak...
                    </div>
                  )}
                </div>
              </ScrollArea>
            </div>
          </div>

          {/* Video Call Section */}
           <div className="flex-1 flex gap-3 min-h-0">
             {/* User's Video */}
             <div className={`flex-1 rounded-2xl border-2 flex flex-col min-h-0 transition-all duration-500 relative bg-[#282a2c] ${
               isUserSpeaking && !mediaState.isMuted ? 
               "border-[#d894fb] shadow-lg shadow-[#d894fb]/30" : 
               mediaState.isMuted ? 
               "border-danger-line/70 shadow-lg shadow-red-500/20" :
               "border-border"
             }`}>
               {/* Ripple Effect for User Speaking */}
               {isUserSpeaking && !mediaState.isMuted && (
                 <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
                   <div className="absolute inset-0 rounded-2xl border-4 border-[#d894fb] opacity-40 animate-ping"></div>
                   <div className="absolute inset-2 rounded-2xl border-2 border-[#d894fb] opacity-60 animate-ping" style={{ animationDelay: '0.3s' }}></div>
                   <div className="absolute inset-4 rounded-2xl border border-[#d894fb] opacity-80 animate-ping" style={{ animationDelay: '0.6s' }}></div>
                 </div>
               )}
               
               <div className="flex-1 relative overflow-hidden rounded-2xl bg-muted min-h-0 w-full h-full">
                 {mediaState.isVideoEnabled && hasLocalStream ? (
                   <video
                     ref={localVideoRef}
                     autoPlay
                     playsInline
                     muted
                     className="absolute inset-0 w-full h-full object-cover bg-black"
                     style={{ transform: 'scaleX(-1)' }}
                     onLoadedMetadata={(e) => {
                       // Metadata loaded
                     }}
                     onPlay={() => {
                       if (localVideoRef.current && localVideoRef.current.paused) {
                         localVideoRef.current.play().catch(e => Sentry.logger.error("Failed to play video", { error: e, component: 'VideoCallInterface' }));
                       }
                     }}
                     onError={(e) => Sentry.logger.error("Local video error", { error: e, component: 'VideoCallInterface' })}
                     onPause={() => {}}
                     onLoadStart={() => {}}
                   />
                 ) : !mediaState.isVideoEnabled ? (
                   <div className="absolute inset-0 flex items-center justify-center bg-muted">
                     <div className="text-center">
                       <VideoOff className="h-16 w-16 mx-auto mb-4 text-ink-faint" />
                       <p className="text-ink-faint">Camera is off</p>
                     </div>
                   </div>
                 ) : (
                   <div className="absolute inset-0 bg-muted bg-opacity-90 flex items-center justify-center">
                     <div className="text-center">
                       <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-border mx-auto mb-4"></div>
                       <p className="text-ink-faint">Starting camera...</p>
                     </div>
                   </div>
                 )}
               </div>

               {/* Audio indicator for user */}
               <div className="absolute bottom-3 left-3 w-8 h-8 transition-opacity duration-300">
                 <img 
                   src={isUserSpeaking && !mediaState.isMuted ? audioSpeaking : audioLines} 
                   alt="Audio indicator"
                   className={`w-full h-full ${isUserSpeaking && !mediaState.isMuted ? 'opacity-100' : 'opacity-50'}`}
                 />
               </div>
             </div>

            {/* AI Agent's Video */}
             <div className={`flex-1 rounded-2xl border-2 flex flex-col min-h-0 transition-all duration-500 relative bg-[#282a2c] ${
               isAgentSpeaking && !agentDisconnected ? 
               "border-[#d894fb] shadow-lg shadow-[#d894fb]/30" : 
               agentDisconnected ? 
               "border-danger-line/70 shadow-lg shadow-red-500/20" :
               "border-border"
             }`}>
              <div className="w-full h-[21px] flex flex-col justify-start items-start flex-shrink-0 p-4">
                <div className="text-white text-base font-medium leading-[21.12px]">
                  AI Interviewer
                </div>
              </div>

              {/* AI Avatar */}
              <div className="flex flex-col justify-center items-center flex-1">
                <div className={`relative transition-all duration-300 ${
                  isAgentSpeaking && !agentDisconnected ? 
                  'w-[280px] h-[280px] scale-110' :
                  agentDisconnected ?
                  'w-[120px] h-[120px] scale-95' :
                  'w-[120px] h-[120px]'
                }`}>
                  {/* Background Animation - only when agent is speaking */}
                  {isAgentSpeaking && !agentDisconnected && (
                    <div className="absolute inset-0 w-full h-full">
                      <Lottie 
                        animationData={hirevoxLogoAnimation}
                        loop={true}
                        className="w-full h-full"
                      />
                    </div>
                  )}
                  
                  {/* Hirevox Logo */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Avatar className="h-[120px] w-[120px] z-10">
                      <AvatarImage src="/hirevox-logo.svg?height=120&width=120" alt="AI Agent"/>
                      <AvatarFallback className={`text-lg transition-all duration-300 ${
                        agentDisconnected ? 'bg-destructive text-white' : 'bg-primary text-primary-foreground'
                      }`}>AI</AvatarFallback>
                    </Avatar>
                  </div>
                </div>
                
                {/* Connection Status */}
                {shouldShowAgentConnecting && (
                  <div className="mt-6">
                    <div className="text-ink-faint text-base font-bold text-center">
                      {connecting ? "Agent is connecting, please wait..." : 
                       !connected ? "Connecting to agent..." :
                       "Agent is getting ready..."}
                    </div>
                  </div>
                )}
              </div>

              {/* Audio indicator for agent */}
              <div className="absolute bottom-3 left-3 w-8 h-8 transition-opacity duration-300">
                <img 
                  src={isAgentSpeaking && !agentDisconnected ? audioSpeaking : audioLines} 
                  alt="Audio indicator"
                  className={`w-full h-full ${isAgentSpeaking && !agentDisconnected ? 'opacity-100' : 'opacity-50'}`}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Screen Share Panel - Bottom Right */}
        <div className={`absolute bottom-6 right-6 bg-[#131314] border border-border rounded-xl transition-all duration-300 shadow-lg ${
          isScreenShareExpanded ? 'w-80 h-60' : 'w-64 h-12'
        }`}>
          {/* Header */}
          <div className="flex justify-between items-center p-3 border-b border-border">
            <div className="text-white text-sm font-semibold flex items-center gap-2">
              <Monitor className="w-4 h-4" />
              Screen Share
            </div>
            <button
              onClick={() => setIsScreenShareExpanded(!isScreenShareExpanded)}
              className="p-1 hover:bg-muted rounded transition-colors"
            >
              <ChevronDown className={`w-4 h-4 text-white transition-transform duration-200 ${
                isScreenShareExpanded ? "" : "rotate-180"
              }`} />
            </button>
          </div>

          {/* Content */}
          {isScreenShareExpanded && (
            <div className="p-3 flex-1">
              <div className="w-full h-44 bg-[#343537] rounded relative overflow-hidden">
                {mediaState.screenSharingActive ? (
                  <>
                    {mediaState.screenShare ? (
                      <video
                        ref={(el) => {
                          if (el && mediaState.screenShare) {
                            if (el.srcObject !== mediaState.screenShare) {
                              cleanupVideoElement(el);
                              el.srcObject = mediaState.screenShare;
                            }
                          }
                        }}
                        autoPlay
                        playsInline
                        muted
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <video
                        ref={screenShareRef}
                        autoPlay
                        playsInline
                        muted
                        className="w-full h-full object-cover"
                      />
                    )}
                    <div className="absolute bottom-2 right-2">
                      <div className="w-2 h-2 bg-good rounded-full animate-pulse"></div>
                    </div>
                  </>
                ) : (
                  <div className="h-full flex items-center justify-center text-ink-faint">
                    <div className="text-center">
                      <Monitor className="h-6 w-6 mx-auto mb-1" />
                      <p className="text-xs">No screen sharing</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Controls */}
      <div className="w-full h-[72px] px-6 py-5 bg-[#131314] border-t border-white/5 flex justify-between items-center flex-shrink-0">
        {/* Left side - Empty spacer */}
        <div className="flex-1"></div>

        {/* Center - Main controls */}
        <div className="flex justify-center items-center gap-5">
          {/* End Call */}
          <button
            onClick={() => setShowEndCallDialog(true)}
            className="
              p-3 bg-[#cc4538] rounded-full flex justify-center items-center
              transition-all duration-200 hover:scale-110 hover:bg-[#dc5548]
              shadow-lg hover:shadow-xl shadow-[#cc4538]/25
            "
            title="End Call"
          >
            <PhoneOff className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* Right side - Empty spacer */}
        <div className="flex-1"></div>
      </div>

      {/* Hidden video elements for remote video and audio */}
      <video
        ref={remoteVideoRef}
        autoPlay
        playsInline
        className="hidden"
      />
      
      <audio ref={remoteAudioRef} autoPlay />

      {/* Wave Animation Container */}
      <div 
        ref={siriWaveRef}
        className="absolute inset-0 pointer-events-none"
        style={{ 
          opacity: isAgentSpeaking && !agentDisconnected ? 0.3 : 0,
          transition: 'opacity 0.3s ease-in-out'
        }}
      />

             {/* Live Subtitles now shown in agent window - overlay disabled */}

      {/* End Call Confirmation Dialog */}
      <AlertDialog open={showEndCallDialog} onOpenChange={setShowEndCallDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>End {isInterviewSession ? 'Interview' : 'Call'}?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to end this {isInterviewSession ? 'interview' : 'call'}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowEndCallDialog(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowEndCallDialog(false);
                onEndCall(isInterviewSession ? "customer-ended-web-session" : "User ended call");
              }}
              className="bg-destructive hover:bg-destructive"
            >
              End {isInterviewSession ? 'Interview' : 'Call'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* All existing overlays and warnings - PRESERVED */}
      <TabChangePenaltyOverlay
        show={showTabChangePenalty}
        onComplete={onTabChangePenaltyComplete || (() => {})}
      />


      <ScreenShareEndedWarning
        show={mediaState.showScreenShareEndedWarning || false}
        reason={mediaState.screenShareEndReason || "track_ended"}
        onClose={onDismissScreenShareWarning}
        violationCount={violations?.filter(v => v.type === "screen_share_lost").length || 1}
        roomDisconnected={!connected && !callActive}
        callId={callId}
        organizationId={organizationId}
      />

      <ExternalMonitorWarning
        show={showExternalMonitorWarning}
        screenCount={externalMonitorScreenCount}
        previousScreenCount={externalMonitorPreviousScreenCount}
        onClose={onDismissExternalMonitorWarning}
        violationCount={violations?.filter(v => v.type === "multiple_monitors").length || 1}
        roomDisconnected={!connected && !callActive}
        callId={callId}
        organizationId={organizationId}
      />

      <CameraDisconnectedWarning
        show={mediaState.showCameraDisconnectedWarning || false}
        reason={mediaState.cameraDisconnectReason || "track_ended"}
        onClose={onDismissCameraWarning}
      />

      <NetworkDisconnectedWarning
        show={networkStatus === 'disconnected'}
      />

      <PoorConnectionWarning
        show={connectionQuality === 'poor'}
      />

      <WindowBlurPenaltyOverlay
        show={showWindowBlurPenalty}
        onComplete={onWindowBlurPenaltyComplete || (() => {})}
      />

      {/* Agent Connection Overlay - Shows loading progress while agent connects */}
      <AgentConnectionOverlay
        show={shouldShowAgentConnecting}
        agentConnected={agentConnected}
        onComplete={() => {
          // This will be handled by the parent component state
        }}
      />

{/* 
      <ViolationsPanel
        violations={violations}
        show={showViolationsPanel}
        callActive={callActive}
      /> */}
    </div>
  );
};
                      
